import { EVENT_REGISTRY, type RegisteredEventName } from "./registry";

export interface ValidateEventResult {
  ok: boolean;
  /** Event name was not found in the registry. Treated as soft-warn, not an error. */
  unregistered?: boolean;
  /** Zod issues if validation failed. */
  issues?: { path: string; message: string }[];
}

export const isProduction =
  process.env.NODE_ENV === "production" ||
  process.env.NEXT_PUBLIC_STATSIG_TIER === "production" ||
  process.env.VERCEL_TARGET_ENV === "production";

const isTest = process.env.NODE_ENV === "test";

/**
 * Validate an event payload against the typed registry.
 *
 * Behavior:
 * - If `eventName` is not a registered event, returns `{ ok: true, unregistered: true }`.
 *   Fires the event but logs a soft-warn so we know about drift.
 * - In test/dev, throws on validation failure (loud) so bad fires surface immediately.
 * - In production, returns `{ ok: false, issues }` so the caller can log to Sentry
 *   but still fire — analytics must not be a SPOF for the user-facing flow.
 */
export function validateEvent(
  eventName: string,
  value: unknown,
  metadata: unknown
): ValidateEventResult {
  const definition = (EVENT_REGISTRY as Record<string, (typeof EVENT_REGISTRY)[RegisteredEventName] | undefined>)[
    eventName
  ];

  if (!definition) {
    return { ok: true, unregistered: true };
  }

  const valueResult = definition.valueSchema.safeParse(value);
  const metadataResult = definition.metadataSchema.safeParse(metadata ?? {});

  if (valueResult.success && metadataResult.success) {
    return { ok: true };
  }

  const issues: { path: string; message: string }[] = [];
  if (!valueResult.success) {
    for (const issue of valueResult.error.issues) {
      issues.push({
        path: ["value", ...issue.path].join("."),
        message: issue.message,
      });
    }
  }
  if (!metadataResult.success) {
    for (const issue of metadataResult.error.issues) {
      issues.push({
        path: ["metadata", ...issue.path].join("."),
        message: issue.message,
      });
    }
  }

  if (isTest || (!isProduction && process.env.STATSIG_VALIDATION_LOUD !== "false")) {
    throw new EventValidationError(eventName, issues);
  }

  return { ok: false, issues };
}

export class EventValidationError extends Error {
  constructor(
    public readonly eventName: string,
    public readonly issues: { path: string; message: string }[]
  ) {
    super(
      `Statsig event "${eventName}" failed schema validation:\n` +
        issues.map((i) => `  - ${i.path}: ${i.message}`).join("\n")
    );
    this.name = "EventValidationError";
  }
}
